import { ValueOf } from 'lib/core/types';

import { TaskAction } from 'lib/common/contexts/ContactContext/constants/taskAction';
import { TChatChanges } from 'lib/common/contexts/ContactContext/types/ChatChanges';
import TCustomerProfile from 'lib/common/contexts/ContactContext/types/CustomerProfile';
import THandleContactChange from 'lib/common/contexts/ContactContext/types/HandleContactChange';
import getCallContactType from 'lib/common/contexts/ContactContext/utils/getCallContactType';
import taskBuilder from 'lib/common/contexts/ContactContext/utils/taskBuilder/index';

import CONNECTION_STATES from 'lib/common/constants/connectionStates';
import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPE from 'lib/common/constants/contactTypes';

import ChatTask from 'lib/common/types/ChatTask';
import TTask from 'lib/common/types/Task';
import { THydratedChat } from 'lib/common/types/chat/HydratedChat';
import { isConference } from 'lib/common/utils/conferenceConnections';

import getTaskType from '../getTaskType';

export default async function updatedTask({
  taskIndex,
  tasks,
  status,
  connectionState,
  contact,
  profile,
  handleContactChange,
  icon,
  getSelectedTaskId,
  newChat,
  hydratedChat,
  taskProps,
  incomingChatNotificationsEnabledRef,
}: {
  taskIndex: number;
  tasks: TTask[];
  status?: ValueOf<typeof CONTACT_STATES>;
  connectionState?: ValueOf<typeof CONNECTION_STATES>;
  contact?: connect.Contact;
  profile?: TCustomerProfile;
  handleContactChange: THandleContactChange;
  icon: string;
  getSelectedTaskId: () => string | undefined;
  newChat?: TChatChanges;
  hydratedChat?: THydratedChat;
  taskProps: any;
  incomingChatNotificationsEnabledRef: React.MutableRefObject<boolean>;
}) {
  const existingTask = tasks[taskIndex];
  const updatedContact = contact || existingTask.contact;
  const type = isConference(updatedContact) ? getCallContactType(updatedContact) : existingTask.type;

  /**
   * Scenario - 1 for queue callbacks when we get two new contacts with the same id
   * Scenario - 2 bypass conference call because the parent call is connected but status of the call has changed to connecting
   * Scenario - 3 bypass preview-dialing to call transition because the task transitions from pending to connecting
   * The above scenario shouldn't be considered as a new task.
   */
  if (
    status === CONTACT_STATES.CONNECTING &&
    type !== CONTACT_TYPE.CONFERENCE_CALL &&
    existingTask.type !== CONTACT_TYPE.PREVIEW_DIALING
  ) {
    const updatedTask = {
      ...existingTask,
      contact: updatedContact,
    };

    return { task: updatedTask, action: TaskAction.UPDATE };
  }

  // We set a task to rejected state manually, but in just the call scenario, connect calls onMissed
  if (status && status === CONTACT_STATES.MISSED && existingTask.status === CONTACT_STATES.REJECTED) {
    return null;
  }

  if (newChat || hydratedChat || existingTask.type === CONTACT_TYPE.CHAT) {
    const updatedChatTask = await taskBuilder.updatedChatTask({
      newChat,
      hydratedChat,
      existingTask: existingTask as ChatTask,
      handleContactChange,
      icon,
      getSelectedTaskId,
      updatedContact,
      taskProps,
      type,
      status,
      connectionState,
      profile,
      incomingChatNotificationsEnabledRef,
    });

    return { task: updatedChatTask, action: TaskAction.UPDATE };
  }

  if (existingTask.type === CONTACT_TYPE.PREVIEW_DIALING) {
    const updatedPreviewDialingTask = await taskBuilder.updatedPreviewDialingTask({
      existingTask,
      updatedContact,
      taskProps,
      status,
      connectionState,
      profile,
    });

    return { task: updatedPreviewDialingTask, action: TaskAction.UPDATE };
  }

  // Base Task
  const updatedTask = taskBuilder.updatedBaseTask({
    existingTask,
    updatedContact,
    taskProps,
    type,
    status,
    connectionState,
    profile,
  });

  return { task: updatedTask, action: TaskAction.UPDATE };
}
